import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Package, ClipboardList, RefreshCw, Warehouse, Settings,
  LogOut, PanelLeftClose, PanelLeftOpen, ChevronRight, User,
  Boxes, ShoppingCart, Truck, FileSpreadsheet, BookOpen, FolderSync,
  Sparkles, Database, Link2, Send, Receipt, ListChecks, Tag,
  MessageSquare, Store, ScanLine, Wrench, FileText, UserCog, X,
  ShieldCheck, Users as UsersIcon, FileEdit, Images
  , Activity, Inbox, CalendarDays, Headphones, BarChart3
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';

const SECTIONS = [
  {
    label: null,
    items: [
      { label: 'Nyheter', path: '/nyheter', icon: Sparkles },
      { label: 'Huskeliste', path: '/huskeliste', icon: ListChecks, badgeKey: 'huskeliste' },
      { label: 'Oversikt', path: '/', icon: LayoutDashboard },
      { label: 'Vaktplan', path: '/vaktplan', icon: CalendarDays },
      { label: 'Kundebestilling', path: '/kundebestilling', icon: Send },
      { label: 'Bestilling & service', path: '/leverandor-oppsett?tab=bestilling', icon: Headphones },
    ],
  },
  {
    label: 'Varebehandling',
    items: [
      { label: 'Produktmaster', path: '/produktmaster', icon: Boxes },
      { label: 'Importer varer', path: '/import', icon: Package },
      { label: 'Oppskriftsbibliotek', path: '/oppskriftsbibliotek', icon: BookOpen },
      { label: 'Oppskrift-innsjekk', path: '/oppskrift-innsjekk', icon: FileText },
      { label: 'Publiseringsplan', path: '/publiseringsplan', icon: ListChecks },
    ],
  },
  {
    label: 'Ordrebehandling',
    items: [
      { label: 'Varemottak', path: '/varemottak', icon: Truck },
      { label: 'Ordrebekreftelse', path: '/ordrebekreftelse', icon: Receipt },
      { label: 'Varer i bestilling', path: '/varer-i-bestilling', icon: ShoppingCart },
      { label: 'Salg', path: '/salg', icon: Tag },
      { label: 'Salgsrapport', path: '/salgsrapport', icon: BarChart3 },
      { label: 'Leverandør-beskjeder', path: '/leverandor-beskjeder', icon: MessageSquare },
      { label: 'E-post innboks', path: '/epost-innboks', icon: Inbox, badgeKey: 'epost_nye' },
      { label: 'Ordreliste', path: '/orders', icon: ClipboardList },
    ],
  },
  {
    label: 'Synkronisering',
    items: [
      { label: 'Garnpakker', path: '/garnpakker', icon: Package },
      { label: 'WooCommerce Sync', path: '/woo-sync', icon: FolderSync },
      { label: 'Media behandling', path: '/media-behandling', icon: Images },
      { label: 'Universal matching', path: '/matching', icon: Database },
      { label: 'Synkroniseringsmaler', path: '/synkroniseringsmaler', icon: RefreshCw },
    ],
  },
  {
    label: 'Lagerstyring',
    items: [
      { label: 'Lagerstyring', path: '/lagerstyring', icon: Warehouse },
      { label: 'Periodisk varetelling', path: '/periodisk-varetelling', icon: ScanLine },
      { label: 'Kasse-samspill', path: '/kasse-samspill', icon: Store },
    ],
  },
  {
    label: 'Innstillinger',
    items: [
      { label: 'Leverandør-oppsett', path: '/leverandor-oppsett', icon: Wrench },
      { label: 'Excel import', path: '/excel-import', icon: FileSpreadsheet },
      { label: 'Maler', path: '/templates', icon: FileText },
      { label: 'Innstillinger', path: '/settings', icon: Settings },
      { label: 'Profil og priser', path: '/profile', icon: UserCog },
      { label: 'Agent notater', path: '/agent-notater', icon: FileText },
    ],
  },
];

const ADMIN_SECTION = {
  label: 'Administrasjon',
  items: [
    { label: 'Admin-oversikt', path: '/admin', icon: ShieldCheck },
    { label: 'Helse', path: '/admin/helse', icon: Activity },
    { label: 'Brukere', path: '/admin/brukere', icon: UsersIcon },
    { label: 'Venteliste', path: '/admin/venteliste', icon: ListChecks },
    { label: 'Landingsside', path: '/admin/innhold', icon: FileEdit },
  ],
};

const STORAGE_KEY = 'rm-sidebar-collapsed';

export default function AppSidebar({ mobileOpen, onMobileClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [huskelisteCount, setHuskelisteCount] = useState(0);
  const [epostVarselCount, setEpostVarselCount] = useState(0);

  useEffect(() => {
    let active = true;
    const fetchCount = async () => {
      try {
        const r = await api.get('/leverandor-komm/huskeliste/count');
        if (active) setHuskelisteCount(r.data?.open_total || 0);
      } catch (e) { /* ignore */ }
      try {
        const r2 = await api.get('/email-inbox/notifications/count');
        if (active) setEpostVarselCount(r2.data?.count || 0);
      } catch (e) { /* ignore */ }
    };
    fetchCount();
    const iv = setInterval(fetchCount, 60000);
    return () => { active = false; clearInterval(iv); };
  }, [location.pathname]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === '1') setCollapsed(true);
    } catch (e) { /* ignore */ }
  }, []);

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem(STORAGE_KEY, next ? '1' : '0'); } catch (e) { /* ignore */ }
      return next;
    });
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    const [pathname, query] = path.split('?');
    if (location.pathname !== pathname) return location.pathname.startsWith(pathname + '/');
    // Lenker med ?tab=… er egne menypunkter (f.eks. Bestilling & service).
    // Andre faner faller tilbake til hovedpunktet, slik at menyen alltid viser hvor du er.
    const sameLink = SECTIONS.concat(ADMIN_SECTION).flatMap(sec => sec.items)
      .some(i => i.path.startsWith(pathname + '?') && location.search.includes(i.path.split('?')[1]));
    if (query) return location.search.includes(query);
    return !sameLink;
  };

  // Admin-only nav section appended at the end if applicable
  const effectiveSections = isAdmin ? [...SECTIONS, ADMIN_SECTION] : SECTIONS;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNav = (path) => {
    navigate(path);
    if (onMobileClose) onMobileClose();
  };

  const widthClass = collapsed ? 'w-[68px]' : 'w-64';

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-foreground/30 backdrop-blur-[2px] z-40 transition-opacity"
          onClick={onMobileClose}
          data-testid="sidebar-backdrop"
        />
      )}

      <aside
        className={`${widthClass} ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0
          fixed lg:sticky top-0 left-0 z-50 h-screen
          bg-sidebar border-r border-sidebar-border
          flex flex-col transition-[width,transform] duration-300 ease-out`}
        data-testid="app-sidebar"
      >
        {/* Header / Logo */}
        <div className={`flex items-center ${collapsed ? 'justify-center px-0' : 'justify-between px-5'} h-[68px] shrink-0 border-b border-sidebar-border`}>
          <button
            onClick={() => handleNav('/')}
            className="flex items-center gap-3 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg"
            data-testid="sidebar-logo-button"
          >
            <div className="w-8 h-8 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-semibold text-sm shadow-sm">
              RM
            </div>
            {!collapsed && (
              <div className="flex flex-col items-start leading-tight">
                <span className="text-[15px] font-semibold tracking-tight text-sidebar-foreground">RetailMaster</span>
                <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Holmsen Design</span>
              </div>
            )}
          </button>
          {!collapsed && (
            <button
              onClick={onMobileClose}
              className="lg:hidden p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-sidebar-accent transition-colors"
              data-testid="sidebar-close-mobile"
              aria-label="Lukk meny"
            >
              <X size={16} strokeWidth={1.75} />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden py-4 px-2">
          {effectiveSections.map((section, sIdx) => (
            <div key={sIdx} className={sIdx > 0 ? 'mt-5' : ''}>
              {section.label && !collapsed && (
                <div className="px-4 mb-1.5 text-[10px] uppercase tracking-[0.15em] font-semibold text-muted-foreground/80">
                  {section.label}
                </div>
              )}
              {section.label && collapsed && (
                <div className="mx-3 my-2 border-t border-sidebar-border" />
              )}
              <ul className="flex flex-col gap-0.5">
                {section.items.map((item) => {
                  const active = isActive(item.path);
                  const Icon = item.icon;
                  const badgeCount = item.badgeKey === 'huskeliste' ? huskelisteCount
                    : item.badgeKey === 'epost_nye' ? epostVarselCount : 0;
                  return (
                    <li key={item.path}>
                      <a
                        href={item.path}
                        onClick={(e) => {
                          // La nettleseren håndtere ny-fane (ctrl/cmd/shift/midtklikk);
                          // ellers SPA-naviger.
                          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                          e.preventDefault();
                          handleNav(item.path);
                        }}
                        title={collapsed ? item.label : undefined}
                        className={`w-full group relative flex items-center gap-3 no-underline ${collapsed ? 'justify-center px-0 mx-1' : 'px-3 mx-1'} py-2 rounded-lg text-sm font-medium
                          transition-colors duration-200 ease-out
                          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
                          ${active
                            ? 'bg-primary text-primary-foreground shadow-sm'
                            : 'text-sidebar-foreground hover:bg-sidebar-accent'}`}
                        data-testid={`sidebar-nav-${item.path.replace(/\//g, '-') || 'home'}`}
                      >
                        <span className="relative shrink-0">
                          <Icon size={17} strokeWidth={1.75} />
                          {badgeCount > 0 && collapsed && (
                            <span className="absolute -top-1.5 -right-1.5 min-w-[15px] h-[15px] px-0.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center"
                              data-testid={`sidebar-badge-${item.badgeKey}-collapsed`}>
                              {badgeCount > 99 ? '99' : badgeCount}
                            </span>
                          )}
                        </span>
                        {!collapsed && <span className="truncate">{item.label}</span>}
                        {!collapsed && badgeCount > 0 && (
                          <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
                            data-testid={`sidebar-badge-${item.badgeKey}`}>
                            {badgeCount > 99 ? '99+' : badgeCount}
                          </span>
                        )}
                        {!collapsed && active && badgeCount === 0 && (
                          <ChevronRight size={14} strokeWidth={2} className="ml-auto opacity-80" />
                        )}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        {/* Footer: user + collapse */}
        <div className="shrink-0 border-t border-sidebar-border p-3">
          {!collapsed ? (
            <div className="flex items-center gap-3 px-2 py-2 rounded-lg bg-card border border-border/60">
              <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-xs font-semibold text-foreground shrink-0">
                {(user?.name || user?.email || '?').slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-foreground truncate">
                  {user?.name || user?.email || 'Bruker'}
                </div>
                {isAdmin && (
                  <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">
                    Admin
                  </div>
                )}
              </div>
              <button
                onClick={handleLogout}
                className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-sidebar-accent transition-colors duration-200 ease-out"
                title="Logg ut"
                data-testid="sidebar-logout-button"
              >
                <LogOut size={15} strokeWidth={1.75} />
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1.5">
              <div
                className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-xs font-semibold text-foreground"
                title={user?.name || user?.email}
              >
                {(user?.name || user?.email || '?').slice(0, 1).toUpperCase()}
              </div>
              <button
                onClick={handleLogout}
                className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-sidebar-accent transition-colors"
                title="Logg ut"
                data-testid="sidebar-logout-button"
              >
                <LogOut size={15} strokeWidth={1.75} />
              </button>
            </div>
          )}

          <button
            onClick={toggleCollapsed}
            className={`hidden lg:flex mt-2 ${collapsed ? 'justify-center' : 'justify-start px-3'} items-center gap-2 w-full py-1.5 rounded-md text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-sidebar-accent transition-colors`}
            data-testid="sidebar-collapse-toggle"
          >
            {collapsed
              ? <PanelLeftOpen size={15} strokeWidth={1.75} />
              : <><PanelLeftClose size={15} strokeWidth={1.75} /><span>Skjul meny</span></>}
          </button>
        </div>
      </aside>
    </>
  );
}
